/**
 * Weekly Plan & Attendance — production page.
 *
 * Managers (admin/manager role) plan each user's week:
 *   - Location · Activity · Hours per day
 * Users (including managers themselves) check-in / check-out with GPS.
 * Variance is flagged automatically when check-in location is > threshold km
 * from the planned location (threshold is configurable in Settings).
 *
 * Three tabs:
 *   Plan          — weekly grid, editable by managers only
 *   Plan + Actual — planned vs actual check-in/out
 *   Variance      — flagged rows
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import {
  Calendar, MapPin, Clock, User as UserIcon, AlertTriangle, Navigation,
  ChevronLeft, ChevronRight, Copy, LogIn, LogOut, Activity as ActivityIcon, Info,
  ClipboardList, CheckCircle2, Edit3, Save, ArrowLeft, Lock, Zap, Sparkles,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

/* ─── utils ──────────────────────────────────────────────────────── */

function mondayOf(d = new Date()) {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const m = new Date(d);
  m.setDate(d.getDate() + diff);
  m.setHours(0, 0, 0, 0);
  return m;
}
function daysOfWeek(startDate) {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    return d;
  });
}
const isoOf = (d) => d.toISOString().slice(0, 10);
const fmtDay = (d) => ({
  label: d.toLocaleDateString('en', { weekday: 'short' }),
  date: d.toLocaleDateString('en', { day: '2-digit', month: 'short' }),
  iso: isoOf(d),
});
const initials = (name) => (name || '?').split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();

/* ─── main page ──────────────────────────────────────────────────── */

export default function WeeklyPlanPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isManager = currentUser?.role === 'admin' || currentUser?.role === 'manager';

  const [weekStart, setWeekStart] = useState(mondayOf());
  const [tab, setTab] = useState('plan');
  const [editCell, setEditCell] = useState(null);
  const [users, setUsers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [summary, setSummary] = useState({});
  const [settings, setSettings] = useState({ locations: [], activities: [], variance_threshold_km: 2 });
  const [loading, setLoading] = useState(false);
  const [bulkModal, setBulkModal] = useState({ open: false, user: null });

  const days = useMemo(() => daysOfWeek(weekStart), [weekStart]);
  const today = isoOf(new Date());
  const weekStartIso = isoOf(weekStart);

  const loadSettings = useCallback(async () => {
    try {
      const r = await api.get('/weekly-plan/settings');
      setSettings(r.data);
    } catch { /* ignore */ }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const r = await api.get('/weekly-plan/users');
      setUsers(r.data.users || []);
    } catch (e) { toast.error('Failed to load users'); }
  }, []);

  const loadWeek = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a, s] = await Promise.all([
        api.get(`/weekly-plan/plan?week_start=${weekStartIso}`),
        api.get(`/weekly-plan/actuals?week_start=${weekStartIso}`),
        api.get(`/weekly-plan/summary?week_start=${weekStartIso}`),
      ]);
      setEntries(p.data.entries || []);
      setCheckins(a.data.checkins || []);
      setSummary(s.data || {});
    } catch (e) { toast.error('Failed to load weekly data'); }
    finally { setLoading(false); }
  }, [weekStartIso]);

  useEffect(() => { loadSettings(); loadUsers(); }, [loadSettings, loadUsers]);
  useEffect(() => { loadWeek(); }, [loadWeek]);

  // Build grid lookup: grid[userId][iso] = entry
  const grid = useMemo(() => {
    const g = {};
    entries.forEach(e => {
      if (!g[e.user_id]) g[e.user_id] = {};
      g[e.user_id][e.date] = e;
    });
    return g;
  }, [entries]);

  // Build actuals lookup
  const actuals = useMemo(() => {
    const a = {};
    checkins.forEach(c => {
      if (!a[c.user_id]) a[c.user_id] = {};
      a[c.user_id][c.date] = c;
    });
    return a;
  }, [checkins]);

  const shiftWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d);
  };

  const saveCell = async (userId, dayIso, body) => {
    try {
      const r = await api.post('/weekly-plan/plan', {
        user_id: userId,
        date: dayIso,
        location: body.location,
        activity: body.activity || '',
        hours: body.hours || 0,
        notes: body.notes || '',
      });
      setEntries(prev => {
        const idx = prev.findIndex(e => e.user_id === userId && e.date === dayIso);
        if (idx >= 0) return prev.map((x, i) => i === idx ? r.data : x);
        return [...prev, r.data];
      });
      setEditCell(null);
      toast.success('Entry saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const copyPreviousWeek = async () => {    const prev = new Date(weekStart);
    prev.setDate(prev.getDate() - 7);
    try {
      const r = await api.post('/weekly-plan/plan/copy', {
        source_week_start: isoOf(prev),
        target_week_start: weekStartIso,
      });
      toast.success(`Copied ${r.data.copied} entries from last week`);
      loadWeek();
    } catch (e) { toast.error(e.response?.data?.detail || 'Copy failed'); }
  };

  const bulkSaveRow = async (user, body) => {
    const targetDays = days.filter((d, i) => body.mondayToFriday ? i < 5 : true);
    try {
      const payload = targetDays.map(d => ({
        user_id: user.id,
        date: isoOf(d),
        location: body.location,
        activity: body.activity || '',
        hours: body.hours || 0,
      }));
      await api.post('/weekly-plan/plan/bulk', payload);
      toast.success(`Filled ${payload.length} days for ${user.name}`);
      setBulkModal({ open: false, user: null });
      loadWeek();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk save failed'); }
  };

  const doCheckInOrOut = () => {};

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="weekly-plan-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Command Center
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 text-white flex items-center justify-center">
                <Calendar className="w-4 h-4" />
              </div>
              <div>
                <h1 className="text-base font-bold text-slate-800">Weekly Plan & Attendance</h1>
                <p className="text-[11px] text-slate-500">
                  {isManager
                    ? "Plan each user's week — location, activity & hours."
                    : 'Your weekly plan is shown below.'}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isManager && (
              <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => {
                const nxt = new Date(mondayOf());
                nxt.setDate(nxt.getDate() + 7);
                setWeekStart(nxt);
                toast.info('Jumped to next week — click any cell to add a plan');
              }} data-testid="weekly-plan-next-week-btn">
                <Sparkles className="w-3.5 h-3.5 mr-1" /> Plan Next Week
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-5 space-y-4">
        {/* Week selector + tabs */}
        <Card className="p-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => shiftWeek(-1)} data-testid="weekly-plan-prev">
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <span className="text-sm font-semibold text-slate-700 min-w-[190px] text-center">
              {fmtDay(days[0]).date} – {fmtDay(days[6]).date} {days[6].getFullYear()}
            </span>
            <Button size="sm" variant="outline" onClick={() => shiftWeek(1)} data-testid="weekly-plan-next">
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => setWeekStart(mondayOf())}>
              This week
            </Button>
            <Button size="sm" variant="outline" onClick={() => {
              const nxt = new Date(mondayOf());
              nxt.setDate(nxt.getDate() + 7);
              setWeekStart(nxt);
            }} data-testid="weekly-plan-nextweek-pill">
              Next week →
            </Button>
            {isManager && tab === 'plan' && (
              <Button size="sm" variant="outline" onClick={copyPreviousWeek} className="ml-2" data-testid="weekly-plan-copy-btn">
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy last week
              </Button>
            )}
          </div>

          <div className="flex">
            {[{ id: 'plan', label: 'Plan', Icon: ClipboardList }].map(t => {
              const Icon = t.Icon;
              return (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`px-3 py-1.5 text-[12px] font-semibold border-b-2 transition-colors flex items-center gap-1 ${tab === t.id ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                  data-testid={`weekly-plan-tab-${t.id}`}>
                  <Icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              );
            })}
          </div>
        </Card>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
          <StatCard Icon={UserIcon} color="violet" label="Users" value={summary.users_total ?? users.length} />
          <StatCard Icon={CheckCircle2} color="emerald" label="Planned today" value={`${summary.planned_today ?? 0} / ${summary.users_total ?? users.length}`} />
        </div>

        {/* Tabs content */}
        {loading && entries.length === 0 ? (
          <Card className="p-10 text-center text-sm text-slate-400">Loading weekly data…</Card>
        ) : users.length === 0 ? (
          <Card className="p-10 text-center text-sm text-slate-500">
            No users to plan. Add users in <button className="underline text-violet-700" onClick={() => navigate('/users')}>User Management</button>.
          </Card>
        ) : tab === 'plan' ? (
          <>
            {isManager && (
              <Card className="p-3 bg-gradient-to-r from-violet-50 to-indigo-50 border-violet-200 mb-3">
                <div className="flex items-start gap-2 text-[12px] text-violet-900">
                  <Info className="w-4 h-4 text-violet-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Planning mode — {fmtDay(days[0]).date} to {fmtDay(days[6]).date}</p>
                    <p className="text-[11px] mt-0.5 opacity-80">
                      <strong>Click any cell</strong> to assign a Location · Activity · Hours for that user/day.
                      Use <strong>Bulk fill row (⚡)</strong> beside each user's name to fill the whole week in one go.
                      <strong> Copy last week</strong> copies all entries forward if only a few tweaks are needed.
                    </p>
                  </div>
                </div>
              </Card>
            )}
            <PlanGrid users={users} days={days} grid={grid} today={today}
              editable={isManager} editCell={editCell} setEditCell={setEditCell}
              saveCell={saveCell} settings={settings}
              onBulkRow={(u) => setBulkModal({ open: true, user: u })} />
          </>
        ) : null}
      </div>

      {/* Bulk fill row modal */}
      <BulkRowModal
        open={bulkModal.open}
        user={bulkModal.user}
        settings={settings}
        onClose={() => setBulkModal({ open: false, user: null })}
        onSave={(body) => bulkSaveRow(bulkModal.user, body)}
      />
    </div>
  );
}

function StatCard({ Icon, color, label, value }) {
  const colors = {
    violet: 'bg-violet-50 text-violet-600 border-violet-200',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    rose: 'bg-rose-50 text-rose-600 border-rose-200',
  };
  return (
    <Card className="p-3 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${colors[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
        <p className="text-lg font-bold text-slate-800 tabular-nums">{value}</p>
      </div>
    </Card>
  );
}

/* ─── Plan grid ───────────────────────────────────────────────────── */

function PlanGrid({ users, days, grid, today, editable, editCell, setEditCell, saveCell, settings, onBulkRow }) {
  return (
    <Card className="overflow-hidden">
      {!editable && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-800 flex items-center gap-2">
          <Lock className="w-3 h-3" /> Read-only — only admin/manager can edit the weekly plan.
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b text-slate-600">
            <tr>
              <th className="sticky left-0 bg-slate-50 px-3 py-2 text-left font-semibold w-48">User</th>
              {days.map((d, i) => {
                const iso = isoOf(d);
                const isToday = iso === today;
                return (
                  <th key={i} className={`px-3 py-2 text-center font-semibold min-w-[150px] ${isToday ? 'bg-violet-50 text-violet-800' : ''}`}>
                    <div>{fmtDay(d).label}</div>
                    <div className="text-[10px] text-slate-400 font-normal">{fmtDay(d).date}</div>
                    {isToday && <div className="text-[9px] uppercase text-violet-600">Today</div>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b hover:bg-slate-50/50">
                <td className="sticky left-0 bg-white px-3 py-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 text-white flex items-center justify-center text-[10px] font-bold">{initials(u.name)}</div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-800 truncate text-[11px]">{u.name}</p>
                      <p className="text-[9px] text-slate-400 uppercase tracking-wider">{u.role}</p>
                    </div>
                    {editable && onBulkRow && (
                      <button
                        onClick={() => onBulkRow(u)}
                        className="p-1 rounded hover:bg-violet-100 text-violet-500 hover:text-violet-700 flex-shrink-0"
                        title="Bulk fill this user's week"
                        data-testid={`weekly-plan-bulk-${u.id}`}>
                        <Zap className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
                {days.map((d, i) => {
                  const iso = isoOf(d);
                  const cell = grid[u.id]?.[iso] || {};
                  const editing = editCell && editCell.userId === u.id && editCell.dayIso === iso;
                  const isOff = cell.location === 'Week Off';
                  return (
                    <td key={i} className="px-2 py-2 align-top">
                      {editing ? (
                        <EditCellForm cell={cell} settings={settings}
                          onSave={(v) => saveCell(u.id, iso, v)}
                          onCancel={() => setEditCell(null)} />
                      ) : (
                        <div
                          onClick={() => editable && setEditCell({ userId: u.id, dayIso: iso })}
                          className={`relative p-2 rounded border transition-colors ${
                            isOff ? 'border-slate-100 bg-slate-50 opacity-60' :
                            !cell.location ? 'border-dashed border-slate-200 bg-slate-50/40 hover:border-violet-300' :
                            editable ? 'border-slate-200 hover:border-violet-300 hover:bg-violet-50/50 cursor-pointer' :
                            'border-slate-200'
                          } ${editable ? 'cursor-pointer' : ''}`}
                          data-testid={`weekly-plan-cell-${u.id}-${i}`}>
                          {cell.location ? (
                            <>
                              <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-700">
                                <MapPin className="w-3 h-3 text-violet-500 flex-shrink-0" />
                                <span className="truncate" title={cell.location}>{cell.location}</span>
                              </div>
                              {!isOff && (
                                <>
                                  <p className="text-[10px] text-slate-500 truncate mt-0.5" title={cell.activity}>{cell.activity}</p>
                                  <div className="flex items-center gap-1 text-[10px] text-slate-400 mt-1">
                                    <Clock className="w-2.5 h-2.5" /> {cell.hours || 0}h
                                  </div>
                                </>
                              )}
                            </>
                          ) : (
                          <div className="text-[11px] text-violet-600 italic text-center py-1 font-semibold">
                              {editable ? '+ add plan' : '—'}
                            </div>
                          )}
                          {editable && cell.location && (
                            <Edit3 className="w-3 h-3 text-slate-300 absolute right-1.5 top-1.5" />
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function EditCellForm({ cell, settings, onSave, onCancel }) {
  const [location, setLocation] = useState(cell.location || '');
  const [activity, setActivity] = useState(cell.activity || '');
  const [hours, setHours] = useState(cell.hours ?? 8);
  const locations = (settings.locations || []).map(l => l.name).concat(['Week Off']);
  const activities = settings.activities || [];
  return (
    <div className="p-2 border-2 border-violet-400 rounded bg-white space-y-1">
      <Select value={location} onValueChange={setLocation}>
        <SelectTrigger className="h-7 text-[11px]" data-testid="weekly-plan-edit-location">
          <SelectValue placeholder="Location" />
        </SelectTrigger>
        <SelectContent>{locations.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={activity} onValueChange={setActivity}>
        <SelectTrigger className="h-7 text-[11px]" data-testid="weekly-plan-edit-activity">
          <SelectValue placeholder="Activity" />
        </SelectTrigger>
        <SelectContent>{activities.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
      </Select>
      <Input type="number" min="0" max="12" step="0.5" value={hours} onChange={e => setHours(Number(e.target.value))}
        placeholder="Hours" className="h-7 text-[11px]" data-testid="weekly-plan-edit-hours" />
      <div className="flex gap-1">
        <button onClick={() => { if (!location) { toast.error('Pick a location'); return; } onSave({ location, activity, hours }); }}
          className="flex-1 px-2 py-1 text-[10px] font-semibold bg-violet-600 text-white rounded hover:bg-violet-700 flex items-center justify-center gap-1"
          data-testid="weekly-plan-edit-save">
          <Save className="w-3 h-3" /> Save
        </button>
        <button onClick={onCancel} className="px-2 py-1 text-[10px] bg-slate-200 text-slate-700 rounded hover:bg-slate-300">
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ─── Plan + Actual grid ──────────────────────────────────────────── */

function ActualGrid({ users, days, grid, actuals, today }) {
  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-2 bg-slate-50 border-b text-[11px] text-slate-600 flex items-center gap-2">
        <Info className="w-3 h-3" /> Side-by-side: planned location + actual GPS check-in/out. Blue = on plan · Rose = variance.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b text-slate-600">
            <tr>
              <th className="sticky left-0 bg-slate-50 px-3 py-2 text-left font-semibold w-48">User</th>
              {days.map((d, i) => (
                <th key={i} className="px-3 py-2 text-center font-semibold min-w-[180px]">
                  <div>{fmtDay(d).label}</div>
                  <div className="text-[10px] text-slate-400 font-normal">{fmtDay(d).date}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b">
                <td className="sticky left-0 bg-white px-3 py-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 text-white flex items-center justify-center text-[10px] font-bold">{initials(u.name)}</div>
                    <p className="text-[11px] font-semibold text-slate-800 truncate">{u.name}</p>
                  </div>
                </td>
                {days.map((d, i) => {
                  const iso = isoOf(d);
                  const plan = grid[u.id]?.[iso] || {};
                  const a = actuals[u.id]?.[iso];
                  const isFuture = iso > today;
                  const isOff = plan.location === 'Week Off';
                  return (
                    <td key={i} className="px-2 py-2 align-top">
                      {isOff ? (
                        <div className="text-[10px] text-slate-300 italic text-center py-2">off</div>
                      ) : isFuture ? (
                        <div className="p-2 rounded border border-dashed border-slate-200 bg-slate-50 text-[10px] text-slate-400 italic text-center">
                          {plan.location ? `Planned: ${plan.location}` : '—'}
                        </div>
                      ) : (
                        <div className={`p-2 rounded border ${a?.variance ? 'border-rose-300 bg-rose-50/40' : a ? 'border-blue-300 bg-blue-50/40' : 'border-slate-200 bg-slate-50/40'}`}>
                          {plan.location && (
                            <div className="flex items-center gap-1 text-[10px] text-slate-500 mb-1">
                              <MapPin className="w-2.5 h-2.5" /> <span className="truncate">{plan.location}</span>
                            </div>
                          )}
                          {a ? (
                            <div className="space-y-0.5 text-[10px]">
                              <div className="flex items-center gap-1">
                                <LogIn className="w-2.5 h-2.5 text-emerald-600" />
                                <span className="font-mono">{a.check_in?.time}</span>
                                <span className={a.variance ? 'text-rose-600 font-semibold truncate' : 'text-slate-600 truncate'}
                                      title={a.check_in?.lat ? `GPS: ${a.check_in.lat.toFixed(4)}, ${a.check_in.lng.toFixed(4)}` : ''}>
                                  @ {a.check_in?.place || '(no label)'}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                {a.check_out ? (
                                  <>
                                    <LogOut className="w-2.5 h-2.5 text-rose-500" />
                                    <span className="font-mono">{a.check_out.time}</span>
                                  </>
                                ) : (
                                  <span className="text-[9px] text-amber-600 italic">still checked in…</span>
                                )}
                              </div>
                              {a.variance && (
                                <div className="flex items-center gap-1 mt-1 text-[10px] text-rose-700 font-semibold">
                                  <AlertTriangle className="w-2.5 h-2.5" /> {a.distance_off_km ? `${a.distance_off_km} km off-route` : 'off-route'}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-[10px] text-slate-400 italic">no check-in</div>
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ─── Variance list ──────────────────────────────────────────────── */

function VarianceList({ weekStart }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/weekly-plan/variance?week_start=${weekStart}`)
      .then(r => { if (active) setRows(r.data.variances || []); })
      .catch(() => toast.error('Failed to load variance'))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [weekStart]);

  return (
    <Card className="p-0 overflow-hidden">
      <div className="px-4 py-2 border-b bg-rose-50/50 text-[11px] text-rose-800 flex items-center gap-2">
        <AlertTriangle className="w-3 h-3" /> {rows.length} variance{rows.length !== 1 ? 's' : ''} this week — check-in location beyond threshold from planned location.
      </div>
      {loading ? (
        <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-sm text-slate-400 italic">No variances this week — everyone on plan.</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">User</th>
              <th className="px-3 py-2 text-left font-semibold">Date</th>
              <th className="px-3 py-2 text-left font-semibold">Planned</th>
              <th className="px-3 py-2 text-left font-semibold">Actual (GPS)</th>
              <th className="px-3 py-2 text-right font-semibold">Distance off</th>
              <th className="px-3 py-2 text-left font-semibold">Check-in / out</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b hover:bg-rose-50/30">
                <td className="px-3 py-2 font-semibold text-slate-800">{r.user_name}</td>
                <td className="px-3 py-2 text-slate-600">{r.date}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1 text-slate-700">
                    <MapPin className="w-3 h-3 text-violet-500" /> {r.planned_location || '—'}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1 text-rose-700 font-semibold">
                    <Navigation className="w-3 h-3" /> {r.check_in?.place || `${r.check_in?.lat?.toFixed(4)}, ${r.check_in?.lng?.toFixed(4)}`}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  {r.distance_off_km != null ? (
                    <Badge variant="outline" className="bg-rose-100 text-rose-800 border-rose-200 text-[10px] tabular-nums">
                      {r.distance_off_km} km
                    </Badge>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2 text-slate-600 font-mono text-[10px]">
                  {r.check_in?.time} → {r.check_out?.time || 'pending'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}


/* ─── Bulk row modal ─────────────────────────────────────────────── */

function BulkRowModal({ open, user, settings, onClose, onSave }) {
  const [location, setLocation] = useState('');
  const [activity, setActivity] = useState('');
  const [hours, setHours] = useState(8);
  const [mondayToFriday, setMondayToFriday] = useState(true);

  useEffect(() => {
    if (user) { setLocation(''); setActivity(''); setHours(8); setMondayToFriday(true); }
  }, [user]);

  if (!user) return null;

  const locations = (settings.locations || []).map(l => l.name);
  const activities = settings.activities || [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-violet-600" />
            Bulk fill — {user.name}
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-slate-500 mb-2">Fill the selected days with the same plan in one click. You can still tweak individual cells afterwards.</p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Location *</Label>
            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger data-testid="bulk-loc"><SelectValue placeholder="Pick a location" /></SelectTrigger>
              <SelectContent>{locations.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Activity</Label>
            <Select value={activity} onValueChange={setActivity}>
              <SelectTrigger data-testid="bulk-act"><SelectValue placeholder="Pick an activity" /></SelectTrigger>
              <SelectContent>{activities.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Hours per day</Label>
            <Input type="number" min="0" max="12" step="0.5" value={hours} onChange={e => setHours(Number(e.target.value))} />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <input type="checkbox" id="bulk-mon-fri" checked={mondayToFriday} onChange={e => setMondayToFriday(e.target.checked)} />
            <Label htmlFor="bulk-mon-fri" className="text-xs cursor-pointer">Only Monday – Friday (uncheck to include Sat & Sun)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-violet-600 hover:bg-violet-700"
            onClick={() => { if (!location) { toast.error('Pick a location'); return; } onSave({ location, activity, hours, mondayToFriday }); }}
            data-testid="bulk-save">
            <Save className="w-4 h-4 mr-1.5" /> Fill {mondayToFriday ? '5 days' : '7 days'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
